/** @module Book copies of a card by hand: variant, language, condition, how many and the price paid. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Minus, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { LocationSelect } from "@/components/location-select";
import { newPurchase, PurchaseFields, purchaseBody } from "@/components/purchase-fields";
import { Sheet, useToast } from "@/components/ui/overlays";
import { variantLabel } from "@/lib/cards";
import { type Condition, CONDITIONS, LANGUAGES } from "@/lib/collection";

type Card = components["schemas"]["CardPrintOut"];

export function AddCopySheet({
  card,
  open,
  onClose,
  initialVariant,
}: {
  card: Card;
  open: boolean;
  onClose: () => void;
  initialVariant: number | null;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const toast = useToast();
  const [variantId, setVariantId] = useState(initialVariant ?? card.variants[0]?.id ?? 0);
  // The printed language of the name is the best guess for the copy in hand.
  const [language, setLanguage] = useState<string>(
    (LANGUAGES as readonly string[]).includes(card.name_lang) ? card.name_lang : "de",
  );
  const [condition, setCondition] = useState<Condition>("NM");
  const [quantity, setQuantity] = useState(1);
  const [withPurchase, setWithPurchase] = useState(false);
  const [location, setLocation] = useState<string | null>(null);
  const [purchase, setPurchase] = useState(newPurchase);
  const purchaseFields = withPurchase ? purchaseBody(purchase) : {};

  const add = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.POST("/api/v1/collection/items", {
        body: {
          variant_id: variantId,
          language,
          condition,
          quantity,
          location_id: location,
          ...purchaseFields,
        },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      for (const key of ["copies", "collection", "dashboard", "set-progress"]) {
        void client.invalidateQueries({ queryKey: [key] });
      }
      toast.show({ text: t("addCopy.done", { count: quantity }), icon: <Check /> }, 3000);
      setQuantity(1);
      setPurchase(newPurchase());
      setWithPurchase(false);
      onClose();
    },
  });

  return (
    <Sheet open={open} onClose={onClose} label={t("addCopy.title")}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h2 className="t-h3">{t("addCopy.title")}</h2>
          <span className="t-sm t-muted">{card.name}</span>
        </div>
        {card.variants.length > 1 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="t-label pb-2">{t("collection.variant")}</legend>
            <div className="flex flex-wrap gap-2">
              {card.variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  className="chip"
                  aria-pressed={variantId === variant.id}
                  onClick={() => {
                    setVariantId(variant.id);
                  }}
                >
                  {variantLabel(variant.variant_key, (key, fallback) => t(key, fallback))}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}
        <fieldset className="flex flex-col gap-2">
          <legend className="t-label pb-2">{t("collection.language")}</legend>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((option) => (
              <button
                key={option}
                type="button"
                className="chip"
                aria-pressed={language === option}
                onClick={() => {
                  setLanguage(option);
                }}
              >
                {option.toUpperCase()}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-2">
          <legend className="t-label pb-2">{t("collection.condition")}</legend>
          <div className="flex flex-wrap gap-2">
            {CONDITIONS.map((option) => (
              <button
                key={option}
                type="button"
                className="chip num"
                aria-pressed={condition === option}
                onClick={() => {
                  setCondition(option);
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </fieldset>
        <div className="row-between">
          <span className="t-label">{t("addCopy.quantity")}</span>
          <span className="row gap-2">
            <button
              type="button"
              className="icon-btn"
              aria-label={t("addCopy.less")}
              disabled={quantity <= 1}
              onClick={() => {
                setQuantity((current) => Math.max(1, current - 1));
              }}
            >
              <Minus />
            </button>
            <span className="num w-8 text-center text-[18px]" aria-live="polite">
              {quantity}
            </span>
            <button
              type="button"
              className="icon-btn"
              aria-label={t("addCopy.more")}
              disabled={quantity >= 100}
              onClick={() => {
                setQuantity((current) => Math.min(100, current + 1));
              }}
            >
              <Plus />
            </button>
          </span>
        </div>
        <LocationSelect value={location} onChange={setLocation} />
        {withPurchase ? (
          <PurchaseFields
            draft={purchase}
            onChange={setPurchase}
            priceLabel={t("purchase.pricePer")}
          />
        ) : (
          <button
            type="button"
            className="btn btn-ghost btn-sm self-start"
            aria-expanded={false}
            onClick={() => {
              setWithPurchase(true);
            }}
          >
            <ChevronDown />
            {t("purchase.add")}
          </button>
        )}
        {add.isError ? (
          <p role="alert" className="t-sm loss">
            {t("common.saveError")}
          </p>
        ) : null}
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={add.isPending || !variantId || purchaseFields === undefined}
          onClick={() => {
            add.mutate();
          }}
        >
          <Plus />
          {t("addCopy.submit", { count: quantity })}
        </button>
      </div>
    </Sheet>
  );
}
